import { useState, useRef } from 'react';
import { plural } from '../lib/utils';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { previewImport, confirmImport, errorMessage } from '../services/api';
import { toast } from 'sonner';
import { 
    Upload,
    FileJson,
    Film,
    HardDrive,
    Truck,
    Building2,
    AlertTriangle,
    CheckCircle2,
    XCircle,
    Loader2,
    ArrowLeft,
    Eye,
    Play,
    RefreshCw
} from 'lucide-react';

export default function ImportData() {
    const navigate = useNavigate();
    const projectsInputRef = useRef(null);
    const mastersInputRef = useRef(null);
    const movementsInputRef = useRef(null);

    const [files, setFiles] = useState({
        projects: null,
        masters: null,
        movements: null
    });

    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [skipDuplicates, setSkipDuplicates] = useState(true);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [results, setResults] = useState(null);

    const handleFileChange = (type, e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.name.endsWith('.json')) {
                toast.error('Veuillez sélectionner un fichier JSON');
                return;
            }
            setFiles(prev => ({ ...prev, [type]: file }));
            setPreview(null);
            setResults(null);
        }
    };

    const removeFile = (type) => {
        setFiles(prev => ({ ...prev, [type]: null }));
        setPreview(null);
        setResults(null);
        
        if (type === 'projects' && projectsInputRef.current) projectsInputRef.current.value = '';
        if (type === 'masters' && mastersInputRef.current) mastersInputRef.current.value = '';
        if (type === 'movements' && movementsInputRef.current) movementsInputRef.current.value = '';
    };

    const handlePreview = async () => {
        if (!files.projects && !files.masters && !files.movements) {
            toast.error('Veuillez sélectionner au moins un fichier');
            return;
        }

        setLoading(true);
        try {
            const formData = new FormData();
            if (files.projects) formData.append('projects_file', files.projects);
            if (files.masters) formData.append('masters_file', files.masters);
            if (files.movements) formData.append('movements_file', files.movements);

            const response = await previewImport(formData);
            setPreview(response.data);
            toast.success('Aperçu généré');
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors de l\'aperçu'));
        } finally {
            setLoading(false);
        }
    };

    const handleImport = async () => {
        setConfirmDialogOpen(false);
        setImporting(true);

        try {
            const formData = new FormData();
            if (files.projects) formData.append('projects_file', files.projects);
            if (files.masters) formData.append('masters_file', files.masters);
            if (files.movements) formData.append('movements_file', files.movements);
            formData.append('skip_duplicates', skipDuplicates);

            const response = await confirmImport(formData);
            setResults(response.data);
            toast.success('Import terminé !');
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors de l\'import'));
        } finally {
            setImporting(false);
        }
    };

    const FileUploadCard = ({ type, label, icon: Icon, file, inputRef }) => (
        <Card className={`bg-card border-border transition-all ${file ? 'border-primary/50 bg-primary/5' : ''}`}>
            <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                    <div className={`p-3 rounded ${file ? 'bg-primary/20 text-primary' : 'bg-secondary text-muted-foreground'}`}>
                        <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-heading font-semibold text-lg">{label}</h3>
                        {file ? (
                            <div className="flex items-center gap-2 mt-2">
                                <Badge variant="secondary" className="font-mono text-xs">
                                    <FileJson className="w-3 h-3 mr-1" />
                                    {file.name}
                                </Badge>
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => removeFile(type)}
                                    aria-label={`Retirer le fichier ${file.name}`}
                                    className="h-8 w-8 p-0 touch:h-11 touch:w-11 text-muted-foreground hover:text-destructive-text"
                                >
                                    <XCircle className="w-4 h-4" />
                                </Button>
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground mt-1">
                                Aucun fichier sélectionné
                            </p>
                        )}
                    </div>
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".json"
                        onChange={(e) => handleFileChange(type, e)}
                        className="hidden"
                    />
                    <Button 
                        variant={file ? "outline" : "default"}
                        size="sm"
                        onClick={() => inputRef.current?.click()}
                        aria-label={`${file ? 'Changer' : 'Choisir'} le fichier : ${label}`}
                    >
                        <Upload className="w-4 h-4 mr-2" />
                        {file ? 'Changer' : 'Choisir'}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );

    return (
        <Layout>
            <div className="p-4 sm:p-6 space-y-6 animate-fade-in" data-testid="import-page">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" aria-label="Retour à la gestion des clients" onClick={() => navigate('/companies')}>
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div>
                        <h1 className="font-heading text-3xl font-bold tracking-tight">
                            IMPORT DE DONNÉES
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Importez vos projets, masters et mouvements depuis des fichiers JSON
                        </p>
                    </div>
                </div>

                {/* File Selection */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <FileUploadCard 
                        type="projects" 
                        label="Projets" 
                        icon={Film} 
                        file={files.projects}
                        inputRef={projectsInputRef}
                    />
                    <FileUploadCard 
                        type="masters" 
                        label="Masters" 
                        icon={HardDrive} 
                        file={files.masters}
                        inputRef={mastersInputRef}
                    />
                    <FileUploadCard 
                        type="movements" 
                        label="Mouvements" 
                        icon={Truck} 
                        file={files.movements}
                        inputRef={movementsInputRef}
                    />
                </div>

                {/* Options */}
                <Card className="bg-card border-border">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <Label htmlFor="f-ignorer-les-doublons" className="font-medium">Ignorer les doublons</Label>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Les éléments existants avec le même nom seront ignorés
                                </p>
                            </div>
                            <Switch id="f-ignorer-les-doublons"
                                checked={skipDuplicates}
                                onCheckedChange={setSkipDuplicates}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Actions */}
                <div className="flex gap-4">
                    <Button 
                        variant="outline" 
                        onClick={handlePreview}
                        disabled={loading || (!files.projects && !files.masters && !files.movements)}
                    >
                        {loading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Eye className="w-4 h-4 mr-2" />
                        )}
                        Aperçu
                    </Button>
                    <Button 
                        onClick={() => setConfirmDialogOpen(true)}
                        disabled={!preview || importing}
                    >
                        {importing ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Play className="w-4 h-4 mr-2" />
                        )}
                        Lancer l'import
                    </Button>
                </div>

                {/* Preview Results */}
                {preview && (
                    <Card className="bg-card border-border">
                        <CardHeader>
                            <CardTitle className="font-heading text-lg flex items-center gap-2">
                                <Eye className="w-5 h-5" />
                                Aperçu de l'import
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Counts */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="p-4 rounded bg-secondary/30 text-center">
                                    <Film className="w-5 h-5 mx-auto mb-2 text-tone-client" />
                                    <p className="text-2xl font-bold">{preview.projects_count}</p>
                                    <p className="text-xs text-muted-foreground">Projets</p>
                                </div>
                                <div className="p-4 rounded bg-secondary/30 text-center">
                                    <HardDrive className="w-5 h-5 mx-auto mb-2 text-tone-stock" />
                                    <p className="text-2xl font-bold">{preview.masters_count}</p>
                                    <p className="text-xs text-muted-foreground">Masters</p>
                                </div>
                                <div className="p-4 rounded bg-secondary/30 text-center">
                                    <Truck className="w-5 h-5 mx-auto mb-2 text-tone-ecrase" />
                                    <p className="text-2xl font-bold">{preview.movements_count}</p>
                                    <p className="text-xs text-muted-foreground">Mouvements</p>
                                </div>
                                <div className="p-4 rounded bg-secondary/30 text-center">
                                    <Building2 className="w-5 h-5 mx-auto mb-2 text-primary" />
                                    <p className="text-2xl font-bold">{preview.new_clients?.length || 0}</p>
                                    <p className="text-xs text-muted-foreground">Nouveaux clients</p>
                                </div>
                            </div>

                            <Separator />

                            {/* New Clients */}
                            {preview.new_clients?.length > 0 && (
                                <div>
                                    <h4 className="font-medium mb-2 flex items-center gap-2">
                                        <Building2 className="w-4 h-4 text-primary" />
                                        Nouveaux clients à créer ({preview.new_clients.length})
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                        {preview.new_clients.map((client, i) => (
                                            <Badge key={i} variant="outline" className="bg-primary/10 border-primary/30">
                                                {client}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Existing Clients */}
                            {preview.existing_clients?.length > 0 && (
                                <div>
                                    <h4 className="font-medium mb-2 flex items-center gap-2">
                                        <CheckCircle2 className="w-4 h-4 text-tone-stock" />
                                        Clients existants ({preview.existing_clients.length})
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                        {preview.existing_clients.map((client, i) => (
                                            <Badge key={i} variant="secondary">
                                                {client}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Duplicates Warning */}
                            {(preview.duplicates?.projects?.length > 0 || preview.duplicates?.masters?.length > 0) && (
                                <div className="p-4 rounded bg-tone-alerte-base/10 border border-tone-alerte-base/30">
                                    <h4 className="font-medium mb-2 flex items-center gap-2 text-tone-alerte">
                                        <AlertTriangle className="w-4 h-4" />
                                        Doublons détectés
                                    </h4>
                                    {preview.duplicates?.projects?.length > 0 && (
                                        <div className="mb-2">
                                            <p className="text-sm text-muted-foreground">
                                                {plural(preview.duplicates.projects.length, 'projet')} : {preview.duplicates.projects.slice(0, 5).join(', ')}
                                                {preview.duplicates.projects.length > 5 && '...'}
                                            </p>
                                        </div>
                                    )}
                                    {preview.duplicates?.masters?.length > 0 && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">
                                                {plural(preview.duplicates.masters.length, 'master existant sera fusionné', 'masters existants seront fusionnés')} : {preview.duplicates.masters.slice(0, 3).join(', ')}
                                                {preview.duplicates.masters.length > 3 && '...'}
                                            </p>
                                        </div>
                                    )}
                                    <p className="text-xs text-muted-foreground mt-2">
                                        Les masters existants seront mis à jour avec les nouvelles données.
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Import Results */}
                {results && (
                    <Card className={`border-2 ${results.errors?.length > 0 ? 'border-tone-alerte-base/50 bg-tone-alerte-base/5' : 'border-tone-stock-base/50 bg-tone-stock-base/5'}`}>
                        <CardHeader>
                            <CardTitle className="font-heading text-lg flex items-center gap-2">
                                {results.errors?.length > 0 ? (
                                    <AlertTriangle className="w-5 h-5 text-tone-alerte" />
                                ) : (
                                    <CheckCircle2 className="w-5 h-5 text-tone-stock" />
                                )}
                                Résultat de l'import
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-tone-stock">{results.projects_imported}</p>
                                    <p className="text-xs text-muted-foreground">Projets importés</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-tone-stock">{results.masters_imported}</p>
                                    <p className="text-xs text-muted-foreground">Masters importés</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-tone-client">{results.masters_merged || 0}</p>
                                    <p className="text-xs text-muted-foreground">Masters fusionnés</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-tone-stock">{results.movements_imported}</p>
                                    <p className="text-xs text-muted-foreground">Mouvements importés</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-tone-neutre">{results.duplicates_skipped}</p>
                                    <p className="text-xs text-muted-foreground">Ignorés (sans n° disque)</p>
                                </div>
                            </div>

                            {results.errors?.length > 0 && (
                                <div className="mt-4">
                                    <h4 className="font-medium mb-2 text-tone-alerte">
                                        Erreurs ({results.errors.length})
                                    </h4>
                                    <div className="max-h-40 overflow-y-auto space-y-1 text-sm text-muted-foreground">
                                        {results.errors.map((error, i) => (
                                            <p key={i} className="text-xs">• {error}</p>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="pt-4 flex gap-4">
                                <Button variant="outline" onClick={() => navigate('/companies')}>
                                    Retour aux clients
                                </Button>
                                <Button onClick={() => {
                                    setFiles({ projects: null, masters: null, movements: null });
                                    setPreview(null);
                                    setResults(null);
                                }}>
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Nouvel import
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Confirm Dialog */}
            <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirmer l'import ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Vous allez importer :
                            <ul className="list-disc list-inside mt-2 space-y-1">
                                {preview?.projects_count > 0 && <li>{plural(preview.projects_count, 'projet')}</li>}
                                {preview?.masters_count > 0 && <li>{plural(preview.masters_count, 'master')}</li>}
                                {preview?.movements_count > 0 && <li>{plural(preview.movements_count, 'mouvement')}</li>}
                                {preview?.new_clients?.length > 0 && <li>{plural(preview.new_clients.length, 'nouveau client', 'nouveaux clients')}</li>}
                            </ul>
                            {skipDuplicates && (
                                <p className="mt-2 text-xs">Les masters existants seront fusionnés.</p>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={handleImport}>
                            Confirmer l'import
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
}
